import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import AttributesComponents from '../../Components/Attributes/Attribute';
import Entitiescomponents from '../../Components/Entities/Entities';
import LinkedEntity from '../../Components/LinkedEntities/LinkedEntity';
import Portal from '../../Components/Portal/Portal';
import PortalDetail from '../../Components/Portal/PortalDetail';
import FailurePublishDialog from '../../Components/PublishToRuntime/FailurePublish';
import InProgressPublishDialog from '../../Components/PublishToRuntime/InProgressPublish';
import { RoleContextProvider } from '../../Components/Roles/Context/RoleContext';
import Roles from '../../Components/Roles/Roles';
import ConfirmationContext from '../../Components/Shared/ConfirmationWithContext/ConfirmationContext';
import WorkspaceAppModelContext from '../../Context/Workspace/WorkspaceAppModelContext';
import usePublishApplication from '../../Hooks/Publish/usePublishApplication';
import { bindShuffleMessageI18nEvents, messagesForPublishInRuntimeIframe } from '../../Components/Shared/ShuffleMessage/MessagesHelper';
import { postMessageToRuntime } from '../../Components/Helpers/Runtime/runtime';
import { useMsal } from '@azure/msal-react';
import { ExportContent } from '../../Components/ProjectSettings/Export';
import ArtifactsContent from '../../Components/Artifacts/Artifacts';
import { ButtonGroup, Dropdown } from 'react-bootstrap';
import { Tooltip } from "@progress/kendo-react-tooltip";
import useInternalUserCheck from '../../Hooks/Misc/useInternalUserCheck';
import ConfirmationDialog from '../../Components/Shared/ConfirmationWithContext/ConfirmationDialog';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import InstanceManager from '../../Components/Instance/InstanceManager/InstanceManager';
import { PropStoreProvider } from '../../Context/PropStore/PropStoreContext';
import Loader from '../../Components/Shared/Loader/loader';
import { useTranslation } from 'react-i18next';
import Planning from '../../Components/Planning/Planning';
import { GlobalContext } from '../../Renderers/GlobalContext';
import AppSettings from '../../Components/ProjectSettings/AppSettings';

function PublishTab({ onPublishClick, onRepublishClick }) {

    const { t } = useTranslation();

    const { confirm, closeDialog } = useContext(ConfirmationContext);

    const handlePublishClick = () => {
        onPublishClick();
    };

    return (
        <div className='workspace__tab__publish'>
            <Tooltip anchorElement="target" position="left" tooltipClassName="kendo-tool-tip-publish">
                <i className="ms-2 me-3 far fa-info-circle" title={`You can update the app to reflect any changes you made in the App Model. If you Republish the app, your app will be reset to default configuration based on the most updated App Model.`}></i>
                <Dropdown className='workspace__tab__publish_dropdown' as={ButtonGroup}>
                    <button
                        className={'btn btn-primary workspace__tab__publish_btn border-right-rounded-0'}
                        onClick={handlePublishClick}
                    >
                        {t("workspace_app_model.tabs.publish")}
                    </button>
                    <Dropdown.Toggle
                        split
                        variant="primary"
                        className={'border-left-rounded-0 workspace__tab__republish_dropdown'}
                    />
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={onRepublishClick}>
                            {t("workspace_app_model.tabs.republish")}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Tooltip>
        </div>
    )
}

function TabRender({ title, icon, onClick }) {
    return (
        <div className='k-drawer-item' onClick={onClick}>
            {!title && <>&nbsp;</>}
            <i className={icon} />
            {title ? title : <>&nbsp;</>}
        </div>
    )
}

function WorkspaceAppModelPage({ project, updateProjectData }) {

    const { subscription, organization, user } = useContext(GlobalContext).state

    const { t, i18n } = useTranslation([
        "translation",
        "common/attribute_type_dropdown"
    ]);

    useEffect(() => {
        bindShuffleMessageI18nEvents()
        i18n.changeLanguage(organization.platformLanguage)
    }, []);

    const msalContext = useMsal();
    const navigate = useNavigate();

    const { confirm, closeDialog } = useContext(ConfirmationContext);
    const { state, mutators } = useContext(WorkspaceAppModelContext);

    const [productionInstance, setProductionInstance] = useState({});
    const [showPublishingDialog, setShowPublishingDialog] = useState(false);

    const [showPublishConfirmationDialog, setShowPublishConfirmationDialog] = useState(false);
    const [showRepublishConfirmationDialog, setShowRepublishConfirmationDialog] = useState(false);
    const [instances, setInstances] = useState([]);
    const [instanceToPublish, setInstanceToPublish] = useState({});
    const [showInstancesTab, setShowInstancesTab] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function getData() {
            try {
                if (subscription && subscription.showInstances) {
                    setShowInstancesTab(true);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        }

        getData();
    }, [])

    const [publishState, error, publishApp, performFullGeneration] = usePublishApplication(
        project._id, organization._id, instanceToPublish, user,
        instanceToPublish && instanceToPublish._id && !instanceToPublish.Published
    )

    const isInternalUser = useInternalUserCheck(user.email)
    const isAdvancedModeOn = project.developerMode;

    function getPublishButtonIndex() {
        let index = 6;
        if (isInternalUser && isAdvancedModeOn) {
            index = 9;
        }
        return index;
    }

    async function handleTabSelect(e) {
        if (e.selected !== getPublishButtonIndex()) {
            const { selectNextTab } = await handleUnsavedChanges();
            if (!selectNextTab) {
                return;
            }
            mutators.setFormsWithChanges({
                entityForm: false,
                attributeForm: false,
                linkedEntityForm: false,
                portalForm: false,
                menuItemForm: false
            });
            mutators.setSelectedTab(e.selected);
        }
    }

    async function handleUnsavedChanges() {
        const anyFormHasChanges = Object.values(state.formsWithChanges).some(value => value);
        if (anyFormHasChanges) {
            const isConfirmed = await confirm({
                title: t("workspace_app_model.unsaved_changes_dialog.title"),
                content: t("workspace_app_model.unsaved_changes_dialog.content"),
                confirmButtonText: t("workspace_app_model.unsaved_changes_dialog.buttons.confirm_button"),
                onConfirm: state.formSaveFunction
            })
            if (!isConfirmed) {
                return {
                    isConfirmed: false,
                    selectNextTab: false
                };
            }
            closeDialog();
            mutators.setFormsWithChanges({
                entityForm: false,
                attributeForm: false,
                linkedEntityForm: false,
                portalForm: false,
                menuItemForm: false
            });
            return {
                isConfirmed: true,
                selectNextTab: true
            };
        }
        return {
            isConfirmed: false,
            selectNextTab: true
        }
    }

    function togglePublishConfirmationDialog() {
        setShowPublishConfirmationDialog(value => !value);
    }

    async function loadInstances() {
        const instances = project.instances;
        // sort instances by type Development, Test, Production
        // if type is same, sort by instanceName
        instances.sort((a, b) => {
            if (a.type === b.type) {
                return a.instanceName.localeCompare(b.instanceName);
            }
            if (a.type === "Development") {
                return -1;
            }
            if (b.type === "Development") {
                return 1;
            }
            if (a.type === "Test") {
                return -1;
            }
            if (b.type === "Test") {
                return 1;
            }
            if (a.type === "Production") {
                return -1;
            }
            if (b.type === "Production") {
                return 1;
            }
            return 0;
        })
        setInstances(instances);
        setInstanceToPublish(instances[0]);
    }

    async function handlePublishClick() {
        loadInstances();
        const { selectNextTab } = await handleUnsavedChanges();
        if (!selectNextTab) {
            return;
        }
        togglePublishConfirmationDialog();
    }

    async function handlePublish() {
        togglePublishConfirmationDialog();
        setShowPublishingDialog(true);
        const result = await publishApp();
        if (result && result.status === "Success") {
            setProductionInstance({
                ...productionInstance,
                Published: true,
                Url: result.data.url
            })
            postMessageToRuntime({
                designerStatus: "Success"
            })
        }
    }

    function toggleRepublishConfirmationDialog() {
        setShowRepublishConfirmationDialog(value => !value);
    }

    async function handleRepublishClick() {
        loadInstances();
        const { selectNextTab } = await handleUnsavedChanges();
        if (!selectNextTab) {
            return;
        }
        toggleRepublishConfirmationDialog();
    }

    async function handleRepublish() {
        toggleRepublishConfirmationDialog();
        setShowPublishingDialog(true);
        const result = await performFullGeneration();
        if (result && result.status === "Success") {
            postMessageToRuntime({
                designerStatus: "Success"
            })
        }
    }

    function handleEntitySelect(entity) {
        mutators.setSelectedEntity(entity);
    }

    function removeSelectedEntity() {
        handleEntitySelect(null);
    }

    function handlePortalSelect(portal) {
        mutators.setSelectedPortal(portal);
    }

    function removeSelectedPortal() {
        mutators.setSelectedPortal(null);
    }

    function handleEntitySelectFromLinkedEntity(entity) {
        handleEntitySelect(entity);
        mutators.setSelectedTab(0);
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <Loader />
            </div>
        )
    }

    return (
        <>
            {showPublishingDialog && publishState === "InProgress" &&
                <InProgressPublishDialog
                    messages={
                        messagesForPublishInRuntimeIframe
                    }
                />
            }
            {showPublishingDialog && publishState === "Failure" &&
                <FailurePublishDialog
                    title={t("workspace_app_model.publish_failed_dialog.title")}
                    error={error}
                    isShowFromWorkspace={false}
                    onClose={() => { setShowPublishingDialog(false) }}
                />
            }
            {
                showPublishConfirmationDialog &&
                <ConfirmationDialog
                    title={t("workspace_app_model.publish_confirmation_dialog.title")}
                    content={
                        <>
                            <p>{t("workspace_app_model.publish_confirmation_dialog.warning_message")}</p>
                            {
                                instances && instances.length > 1 &&
                                <div>
                                    <label className='form-label' id="instance-to-update-label">
                                        {t("workspace_app_model.publish_confirmation_dialog.choose_instance_label")}
                                    </label>
                                    <DropDownList
                                        ariaLabelledBy='instance-to-update-label'
                                        className='w-100'
                                        data={instances}
                                        value={instanceToPublish}
                                        textField="instanceName"
                                        dataItemKey="_id"
                                        onChange={(e) => {
                                            setInstanceToPublish(e.target.value)
                                        }}
                                    />
                                </div>
                            }
                        </>
                    }
                    onCancel={togglePublishConfirmationDialog}
                    onToggle={togglePublishConfirmationDialog}
                    onConfirm={handlePublish}
                    confirmButtonDisabled={instanceToPublish._id === undefined}
                    cancelButtonText={t("workspace_app_model.publish_confirmation_dialog.buttons.cancel_button")}
                    confirmButtonText={t("workspace_app_model.publish_confirmation_dialog.buttons.confirm_button")}
                />
            }
            {
                showRepublishConfirmationDialog &&
                <ConfirmationDialog
                    title={t("workspace_app_model.republish_confirmation_dialog.title")}
                    content={
                        <>
                            <p>{t("workspace_app_model.republish_confirmation_dialog.warning_message_1")}</p>
                            <p>{t("workspace_app_model.republish_confirmation_dialog.warning_message_2")}</p>
                            {
                                instances && instances.length > 1 &&
                                <div>
                                    <label className='form-label' id="instance-to-update-label">{t("workspace_app_model.republish_confirmation_dialog.choose_instance_label")}</label>
                                    <DropDownList
                                        ariaLabelledBy='instance-to-update-label'
                                        className='w-100'
                                        data={instances}
                                        value={instanceToPublish}
                                        textField="instanceName"
                                        dataItemKey="_id"
                                        onChange={(e) => {
                                            setInstanceToPublish(e.target.value)
                                        }}
                                    />
                                </div>
                            }
                        </>
                    }
                    onCancel={toggleRepublishConfirmationDialog}
                    onToggle={toggleRepublishConfirmationDialog}
                    onConfirm={handleRepublish}
                    confirmButtonDisabled={instanceToPublish._id === undefined}
                    cancelButtonText={t("workspace_app_model.republish_confirmation_dialog.buttons.cancel_button")}
                    confirmButtonText={t("workspace_app_model.republish_confirmation_dialog.buttons.confirm_button")}
                />
            }
            <TabStrip
                selected={state.selectedTab}
                onSelect={handleTabSelect}
                animation={false}
                className="h-100 workspace-app-model-tab-strip checkRuntime">
                <TabStripTab
                    title={
                        <TabRender
                            icon={"fas fa-dice-d6"}
                            title={t("workspace_app_model.tabs.objects")}
                            hasChanges={
                                state.formsWithChanges.entityForm ||
                                state.formsWithChanges.attributeForm
                            }
                            onClick={removeSelectedEntity}
                        />
                    }
                    contentClassName='h-100'>
                    {
                        state.selectedEntity && state.selectedEntity._id ?
                            <AttributesComponents
                                useSuspense={false}
                                subscription={subscription}
                                updateUser={() => { }}
                                user={user}
                                organization={organization}
                                project={project}
                                entity={state.selectedEntity}
                                selectedSysName={state.selectedAttribute?.sysName}
                                selectedTab={0}
                                selectedId={""}
                                selectedPageName={""}
                                selectedComponentId={""}
                                isAccessedFromWorkspace={true}
                                isUserEntity={state.selectedEntity.isUserEntity}
                                mutators={mutators}
                                showAIButtons={organization.isAIEnabled} />
                            :
                            <Entitiescomponents
                                useSuspense={false}
                                organization={organization}
                                subscription={subscription}
                                project={project}
                                updateUser={() => { }}
                                sysName=""
                                user={user}
                                selectedTab={0}
                                navigate={navigate}
                                showRecommendationPopup={false}
                                updateSearchParams={() => { }}
                                isAccessedFromWorkspace={true}
                                selectEntity={handleEntitySelect}
                                showAIButtons={organization.isAIEnabled} />
                    }
                </TabStripTab>
                <TabStripTab title={<TabRender icon="fas fa-project-diagram" title={t("workspace_app_model.tabs.relations")} />}>
                    <LinkedEntity
                        useSuspense={false}
                        user={user}
                        advancedMode={project.developerMode}
                        organization={organization}
                        organizationId={organization._id}
                        orgName={organization.orgName}
                        projectId={project._id}
                        projectName={project.name}
                        isAccessedFromWorkspace={true}
                        selectEntity={handleEntitySelectFromLinkedEntity}
                        showAIButtons={organization.isAIEnabled} />
                </TabStripTab>
                <TabStripTab title={<TabRender icon="fas fa-user-friends" title={t("workspace_app_model.tabs.roles")} />}>
                    <RoleContextProvider>
                        <Roles
                            project={project}
                            user={user} />
                    </RoleContextProvider>
                </TabStripTab>
                <TabStripTab
                    title={<TabRender icon="fas fa-chalkboard" title={t("workspace_app_model.tabs.portals")} onClick={removeSelectedPortal} />}>
                    {
                        state.selectedPortal && state.selectedPortal._id ?
                            <PortalDetail
                                useSuspense={false}
                                project={project}
                                organization={organization}
                                portal={state.selectedPortal}
                                user={user}
                                selectedTab={1}
                                isAccessedFromWorkspace={true}
                                mutators={mutators} />
                            :
                            <Portal
                                useSuspense={false}
                                organization={organization}
                                user={user}
                                project={project}
                                isAccessedFromWorkspace={true}
                                selectPortal={handlePortalSelect}
                            />
                    }
                </TabStripTab>
                <TabStripTab title={<TabRender icon="fa-solid fa-globe" title={t("workspace_app_model.tabs.language_settings")} />}>
                    <AppSettings projectData={project} updateProjectData={updateProjectData} />
                </TabStripTab>
                {
                    isInternalUser && isAdvancedModeOn &&
                    <TabStripTab title={<TabRender title="Planning" />}>
                        <Planning
                            organizationId={organization._id}
                            projectRole={"Admin"}
                            orgName={organization.orgName}
                            projectId={project._id}
                            projectName={project.name}
                            user={user}
                        />
                    </TabStripTab>
                }
                <TabStripTab title={<TabRender icon="fa-solid fa-database" title={t("workspace_app_model.tabs.instances")} />}>
                    <PropStoreProvider
                        value={{
                            organizationId: organization._id,
                            projectName: project.name,
                            projectId: project._id,
                            project: project,
                            organizationUrl: organization.orgUrl
                        }}
                    >
                        <InstanceManager updateProjectData={(data) => {
                            updateProjectData({
                                ...project,
                                instances: data
                            })
                        }} />
                    </PropStoreProvider>
                </TabStripTab>
                {
                    isInternalUser && isAdvancedModeOn &&
                    <TabStripTab title={<TabRender icon="fas fa-download" />}>
                        <ExportContent
                            organisationId={organization._id}
                            orgName={organization.orgName}
                            projectName={project.name}
                            projectId={project._id} />
                    </TabStripTab>
                }
                {
                    isInternalUser && isAdvancedModeOn &&
                    <TabStripTab title={<TabRender icon="fas fa-send-back" />}>
                        <ArtifactsContent
                            projectId={project._id} />
                    </TabStripTab>
                }
                <TabStripTab
                    title={
                        <PublishTab
                            onPublishClick={handlePublishClick}
                            onRepublishClick={handleRepublishClick} />
                    } />
            </TabStrip>
        </>
    )
}

export default WorkspaceAppModelPage;